// src/utils/autoText.ts

type AutoTextOpts = {
  minPx?: number;      // floor
  maxPx?: number;      // ceiling
  targetLines?: number; // desired visible lines in the column
  basePx?: number;     // starting guess for probe
  lineHeight?: number; // line-height multiple
};

const probeCache = new WeakMap<HTMLElement, number>(); // px per char
const rafState = new WeakMap<HTMLElement, number>();   // pending rAF id

function computeProbePxPerChar(host: HTMLElement, basePx: number): number {
  const cached = probeCache.get(host);
  if (cached) return cached;

  const probe = document.createElement("span");
  probe.textContent = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.whiteSpace = "nowrap";
  probe.style.fontSize = `${basePx}px`;
  // inherit font from host
  probe.style.fontFamily = getComputedStyle(host).fontFamily || "inherit";
  host.appendChild(probe);
  const pxPerCh = probe.getBoundingClientRect().width / probe.textContent!.length;
  host.removeChild(probe);

  // avoid zero/NaN accidents
  const safe = pxPerCh > 0 ? pxPerCh : 1;
  probeCache.set(host, safe);
  return safe;
}

export function attachAutoTextScale(contentEl: HTMLElement, opts: AutoTextOpts = {}): () => void {
  const {
    minPx = 15,
    maxPx = 28,
    targetLines = 8,     // tweak per design
    basePx = 18,
    lineHeight = 1.25,
  } = opts;

  // style invariants
  contentEl.style.setProperty("--auto-lh", String(lineHeight));
  (contentEl.style as any).lineHeight = "var(--auto-lh)";

  const measureNow = () => {
    const host = contentEl; // the .content node
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Height-driven size: fs ≈ height / (targetLines * lineHeight)
    let fsH = rect.height / (targetLines * lineHeight);

    // Width-driven size: aim for ~65ch lines
    const pxPerCh = computeProbePxPerChar(host, basePx);
    const idealCPL = 65;
    const fsW = (rect.width / (idealCPL * pxPerCh)) * basePx;

    // Blend both constraints
    let fs = (fsH * 0.55) + (fsW * 0.45);

    // Clamp
    fs = Math.max(minPx, Math.min(maxPx, fs));

    host.style.setProperty("--auto-fs", `${fs}px`);
    host.style.fontSize = `var(--auto-fs)`;
  };

  const scheduleMeasure = () => {
    if (rafState.get(contentEl)) return;
    const id = requestAnimationFrame(() => {
      rafState.delete(contentEl);
      // reset probe on major font family changes
      probeCache.delete(contentEl);
      measureNow();
    });
    rafState.set(contentEl, id);
  };

  // initial + observe
  measureNow();
  const ro = new ResizeObserver(scheduleMeasure);
  ro.observe(contentEl);
  if (contentEl.parentElement) ro.observe(contentEl.parentElement);

  // expose a disposer
  return () => {
    try {
      const id = rafState.get(contentEl);
      if (id) cancelAnimationFrame(id);
    } catch {}
    try { ro.disconnect(); } catch {}
  };
}